import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { messages } from '@databases/intl/messages';
import { ScrollPosition } from '@databases/layout/scroll-position';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectHasActiveSaveOperation } from '@databases/redux/changesets/selectors';
import { DatabaseFieldType } from '@databases/types/database';
import {
  DEFAULT_DATABASE_VIEW_SETTINGS,
  MIN_CARD_WIDTH,
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { isEmptyValue } from '@databases/utils/database/field-types';
import {
  createField,
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getExistingFieldNames,
  getUniqueFieldName,
} from '@databases/utils/database/utils';
import { ViewChangeTypes, getGroupField } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useFieldLoader from '@databases/views/useFieldLoader';
import useView from '@databases/views/useView';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import errorIllustration from '../../../../assets/generic_error_dark.svg';
import { calculateGridColumns } from '../../../utils';
import CompactEmptyState from '../../CompactEmptyState';
import { useScope } from '../../Scope';
import { FIELD_TYPES } from '../../field-types/fieldTypes';
import { CONTAINER_PADDING } from '../entry-card/height';

import BoardLayoutAddOptionButton from './BoardLayoutAddOptionButton';
import BoardLayoutColumn from './BoardLayoutColumn';

const GRID_GAP = 8;
const ADD_COLUMN_WIDTH = 40;

const Grid = styled.div<{ columns: number; columnWidth: number; showAddButton: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns:
    repeat(${(props) => props.columns}, ${(props) => props.columnWidth}px)
    ${(props) => (props.showAddButton ? `${ADD_COLUMN_WIDTH}px` : '')};
  gap: ${GRID_GAP}px;
  padding: ${CONTAINER_PADDING}px;
`;

export interface BoardLayoutProps {
  contentId: string;
  isContentEditable: boolean;
  canEditFields: boolean;
  canEditContent: boolean;
  renderSection: ScrollPosition;
  containerWidth: number;
}

const BoardLayout: FunctionComponent<BoardLayoutProps> = ({
  contentId,
  isContentEditable,
  canEditFields,
  canEditContent,
  renderSection,
  containerWidth,
}) => {
  const scope = useScope();
  const { view, setViewChange } = useView();
  const client = useAnalytics();
  const [database, groupField, hasUndefinedValue, hasActiveSaveOperation] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdRequired(state, {
        contentId,
      });
      const groupField = getGroupField(database, view.settings.layout);
      return [
        database,
        groupField,
        groupField &&
          database.entries.some((entryId) => {
            const entry = getDatabaseEntryRequired(database, entryId);
            const value = getConvertedDatabaseValue(database, groupField, entry, state);
            return isEmptyValue(
              { database: database, field: groupField, entry, value },
              state
            );
          }),
        selectHasActiveSaveOperation(state, { contentId }),
      ] as const;
    });

  // Once there is an undefined value, show the undefined column until the database is saved again
  const [showUndefinedColumn, setShowUndefinedColumn] = useState(hasUndefinedValue);
  useEffect(() => {
    if (!showUndefinedColumn && hasUndefinedValue) {
      setShowUndefinedColumn(true);
    }
  }, [hasUndefinedValue]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hasActiveSaveOperation && showUndefinedColumn !== hasUndefinedValue) {
      setShowUndefinedColumn(hasUndefinedValue);
    }
  }, [hasActiveSaveOperation]); // eslint-disable-line react-hooks/exhaustive-deps

  const showAddButton = canEditFields;

  const { columnWidth } = calculateGridColumns({
    containerWidth,
    containerPadding: [
      CONTAINER_PADDING,
      CONTAINER_PADDING + (showAddButton ? GRID_GAP + ADD_COLUMN_WIDTH : 0),
    ],
    gridGap: GRID_GAP,
    minColumnWidth:
      MIN_CARD_WIDTH[(view.settings ?? DEFAULT_DATABASE_VIEW_SETTINGS).layout.cardWidth],
  });

  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const handleCreateSelectField = useCallback(async () => {
    const fieldLabel = formatMessage(
      messages['field-settings.field-name-inline-edit-default-text'],
      {
        defaultFieldName: formatMessage(FIELD_TYPES[DatabaseFieldType.SELECT].label),
      }
    );
    const fieldName = getUniqueFieldName(fieldLabel, getExistingFieldNames(database));
    const field = createField(DatabaseFieldType.SELECT, {
      name: fieldName,
    });
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.ADD_FIELD,
        field,
        currentViewId: view.id,
      })
    );
    setViewChange({
      type: ViewChangeTypes.LAYOUT_GROUP_FIELD_ID,
      value: field.id,
    });

    client.sendAddTagFieldEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
    });

    client.sendCreateDatabaseFieldEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'boardLayout',
    });

    client.sendDatabaseFieldOptionUpdatedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'boardLayout',
      isNewField: true,
      fieldSelection: DatabaseFieldType.SELECT,
    });
  }, [
    client,
    contentId,
    database,
    dispatch,
    formatMessage,
    scope,
    setViewChange,
    view.id,
  ]);

  useFieldLoader({ contentId, fieldType: 'group' });
  return (
    <>
      {!groupField ? (
        <CompactEmptyState
          heading={formatMessage(messages['board-layout.empty-state-heading'])}
          description={formatMessage(messages['board-layout.empty-state-description'])}
          imageUrl={errorIllustration}
          buttonText={
            canEditFields
              ? formatMessage(messages['board-layout.empty-state-button-text'])
              : undefined
          }
          onButtonClick={canEditFields ? handleCreateSelectField : undefined}
        />
      ) : (
        <Grid
          columns={
            (showUndefinedColumn ? 1 : 0) + groupField.settings.selectOptions.length
          }
          columnWidth={columnWidth}
          showAddButton={showAddButton}
        >
          {[
            ...(showUndefinedColumn ? [undefined] : []),
            ...groupField.settings.selectOptions,
          ].map((option) => (
            <BoardLayoutColumn
              key={option ? option.id ?? option.label : ''}
              contentId={contentId}
              isContentEditable={isContentEditable}
              canEditFields={canEditFields}
              canEditContent={canEditContent}
              viewSettings={view.settings}
              option={option}
              renderSection={renderSection}
              columnWidth={columnWidth}
              currentViewId={view.id}
            />
          ))}
          {canEditFields && (
            <BoardLayoutAddOptionButton
              contentId={contentId}
              viewSettings={view.settings}
            />
          )}
        </Grid>
      )}
    </>
  );
};

export default BoardLayout;
